import {AttendanceStatusKey, STATUS_RING_ORDER, normalizeStatusKey} from './attendanceStatusTheme'
import {AttendanceParsedContent} from './parseAttendanceAnswer'

export type AttendanceDayEntry = {
  label: string
  status: string
  statusKey: AttendanceStatusKey
}

export type AttendanceStatusSegment = {
  key: AttendanceStatusKey
  label: string
  count: number
  /** Whether the attendance rate is taken over this status. `false` for the ones that are
   *  reportable but sit outside the rate — Unmarked (no mark exists at all) and Checkout (an event
   *  on a day that already carries its own present/late mark). The ring must chart only the
   *  statuses inside the rate, or its arc and the percentage in its own centre are drawn over
   *  different totals: 103 attended across 120 slices reads 85.8% beside a label correctly saying
   *  86.6% of 119 marks. Undefined means "inside the rate", which is what every caller predating
   *  this field meant. */
  countsTowardRate?: boolean
}

export type AttendanceVisualSummary = {
  mode: 'daily' | 'stats' | 'generic'
  totalRecords: number
  statusSegments: AttendanceStatusSegment[]
  presentCount: number
  presentPercent: number | null
  dayEntries: AttendanceDayEntry[]
  statPairs: {metric: string; value: string}[]
  /** What `totalRecords` counts, when the backend named it (e.g. `days absent`). Falls back to a
   *  generic "records" label where absent. */
  headlineLabel?: string | null
  /** Statuses whose counts are genuinely known, for the comparison bars. Distinct from
   *  `statusSegments`: a status may legitimately be 0 here, whereas a status the query never
   *  measured is omitted entirely rather than drawn as an empty bar. */
  barSegments?: AttendanceStatusSegment[]
}

function findColumn(columns: string[], candidates: string[]): string | null {
  const lower = columns.map((c) => c.toLowerCase())
  for (const candidate of candidates) {
    const idx = lower.indexOf(candidate.toLowerCase())
    if (idx >= 0) return columns[idx]
  }
  return null
}

function parseDateSortKey(label: string): number {
  const iso = label.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return Date.parse(`${iso[1]}-${iso[2]}-${iso[3]}T00:00:00`)

  const d = Date.parse(label)
  return Number.isNaN(d) ? 0 : d
}

function buildStatusSegments(
  rows: Record<string, string>[],
  statusCol: string | null
): AttendanceStatusSegment[] {
  if (!statusCol) return []

  const counts = new Map<AttendanceStatusKey, {label: string; count: number}>()

  for (const row of rows) {
    const raw = (row[statusCol] ?? '').trim()
    if (!raw) continue
    const key = normalizeStatusKey(raw)
    const existing = counts.get(key)
    if (existing) {
      existing.count += 1
    } else {
      counts.set(key, {label: raw, count: 1})
    }
  }

  const segments: AttendanceStatusSegment[] = []
  for (const key of STATUS_RING_ORDER) {
    const entry = counts.get(key)
    if (entry && entry.count > 0) {
      segments.push({key, label: entry.label, count: entry.count})
    }
  }

  const otherKeys = [...counts.keys()].filter((k) => !STATUS_RING_ORDER.includes(k))
  for (const key of otherKeys) {
    const entry = counts.get(key)!
    segments.push({key, label: entry.label, count: entry.count})
  }

  return segments
}

export function buildStatusSegmentsFromRows(
  rows: Record<string, unknown>[],
  statusKey = 'status'
): AttendanceStatusSegment[] {
  if (!rows.length) return []

  const normalized = rows.map((row) => {
    const out: Record<string, string> = {}
    for (const [key, value] of Object.entries(row)) {
      out[key] = value == null || value === '' ? '' : String(value)
    }
    return out
  })

  return buildStatusSegments(normalized, statusKey)
}

function isPresentKey(key: AttendanceStatusKey): boolean {
  return key === 'present' || key === 'excused'
}

export function buildAttendanceVisualSummary(
  content: AttendanceParsedContent
): AttendanceVisualSummary | null {
  const {columns, rows, kind} = content
  if (!rows.length) return null

  if (kind === 'stats' || (columns.includes('Metric') && columns.includes('Value'))) {
    const statPairs = rows.map((row) => ({
      metric: row.Metric ?? row[columns[0]] ?? '',
      value: row.Value ?? row[columns[1]] ?? '',
    }))
    return {
      mode: 'stats',
      totalRecords: rows.length,
      statusSegments: [],
      presentCount: 0,
      presentPercent: null,
      dayEntries: [],
      statPairs,
    }
  }

  const dateCol = findColumn(columns, ['date', 'day', 'attendance date'])
  const statusCol = findColumn(columns, ['status', 'attendance', 'remark'])
  const nameCol = findColumn(columns, ['name', 'student', 'student name'])

  const statusSegments = buildStatusSegments(rows, statusCol)
  const presentCount = statusSegments
    .filter((s) => isPresentKey(s.key))
    .reduce((sum, s) => sum + s.count, 0)

  const countable = statusSegments.reduce((sum, s) => sum + s.count, 0)
  // One decimal, like the V2 path and the backend tally — a whole number here
  // would show 66% against a summary that says 65.5%.
  const presentPercent = countable > 0 ? Math.round((presentCount / countable) * 1000) / 10 : null

  const labelCol = dateCol ?? nameCol ?? columns[0]
  const dayEntries: AttendanceDayEntry[] = rows
    .map((row) => {
      const label = (row[labelCol] ?? '').trim()
      const status = statusCol ? (row[statusCol] ?? '').trim() : ''
      return {
        label,
        status,
        statusKey: normalizeStatusKey(status),
      }
    })
    .filter((e) => e.label)
    .sort((a, b) => parseDateSortKey(b.label) - parseDateSortKey(a.label))

  const mode =
    dateCol && statusCol ? 'daily' : statusCol || statusSegments.length ? 'daily' : 'generic'

  return {
    mode,
    totalRecords: rows.length,
    statusSegments,
    presentCount,
    presentPercent,
    dayEntries,
    statPairs: [],
  }
}

export function canShowAttendanceVisual(content: AttendanceParsedContent): boolean {
  return buildAttendanceVisualSummary(content) !== null
}
